/**
 * 双人PK游戏场景音频管理器
 * 倒计时：预录柔和人声 WAV
 * 进行中：Web Audio 无缝循环柔和欢快 BGM
 * 胜利：预录柔和人声 + 轻柔庆祝音
 * 一方完成：Web Audio 无缝循环紧迫危机提醒
 */

/** 音频固定路径（public/audio 目录，打包后为 dist/audio，文件名不带 hash） */
const AUDIO_BASE = "/audio";

const SCENE_CONFIG = {
  playing: { src: `${AUDIO_BASE}/playing.wav`, volume: 0.1, loop: true },
  crisis: { src: `${AUDIO_BASE}/near-win.wav`, volume: 0.48, loop: true },
  results: { src: `${AUDIO_BASE}/results.wav`, volume: 0.72 },
};

/** 胜利预录人声（略慢播放，更饱满） */
const RESULTS_VOICE_RATE = 1.15;
const RESULTS_VOICE = {
  player1: {
    src: `${AUDIO_BASE}/results-voice-p1.wav`,
    volume: 0.9,
    playbackRate: RESULTS_VOICE_RATE,
  },
  player2: {
    src: `${AUDIO_BASE}/results-voice-p2.wav`,
    volume: 0.9,
    playbackRate: RESULTS_VOICE_RATE,
  },
};

/** 限时赛最后 10 秒：预录人声「还剩十秒」 */
const TIME_LIMIT_WARNING = {
  src: `${AUDIO_BASE}/time-warning.wav`,
  volume: 0.85,
};

/** 答题反馈提示音（相对 BGM 更突出） */
const ANSWER_FEEDBACK = {
  correct: { src: `${AUDIO_BASE}/answer-correct.wav`, volume: 0.95 },
  wrong: { src: `${AUDIO_BASE}/answer-wrong.wav`, volume: 0.9 },
};

const COUNTDOWN_AUDIO = {
  3: { src: `${AUDIO_BASE}/countdown-3.wav`, volume: 0.88 },
  2: { src: `${AUDIO_BASE}/countdown-2.wav`, volume: 0.88 },
  1: { src: `${AUDIO_BASE}/countdown-1.wav`, volume: 0.88 },
  0: { src: `${AUDIO_BASE}/countdown-0.wav`, volume: 1 },
};

const VOICE_PREFER = ["Xiaoxiao", "Xiaoyi", "Yunxi", "Yunyang", "Huihui", "Ting-Ting"];

/**
 * 合成背景音乐库（Web Audio 实时合成，无需音频文件，原创旋律无版权风险）
 * 完整编曲（A-B 段 16 小节结构）：
 * 主旋律（高八度加倍+重音力度）+ 和弦铺底 pad + 低音 + 琶音 + 打击乐（含小节末鼓花）+ 混响
 * melody=主旋律，bass=低音，chords=和弦（琶音与铺底共用）
 * drums=打击乐（拍位置，4 拍一小节循环），fill=每 every 小节末的鼓花
 * f=频率(Hz)，b=拍数，f=0 表示休止符
 */
const SYNTH_BGM = {
  // 欢快：原创旋律，C-G-Am-F 段落推进，明快跳跃
  sunny: {
    bpm: 124,
    volume: 0.12,
    filterFreq: 2400,
    melodyWave: "triangle",
    detune: true, // 双振荡器轻微失谐，音色更厚
    melodyOctave: true, // 旋律高八度加倍
    accent: true, // 小节头重音
    padVolume: 0.22, // 和弦铺底音量
    padWave: "triangle",
    arpEvery: 1, // 琶音每拍一音
    arpVolume: 0.3,
    arpWave: "sine",
    reverb: { delay: 0.23, feedback: 0.3, wet: 0.12 },
    melody: [
      // A 段（8 小节）
      { f: 330, b: 1 }, { f: 392, b: 1 }, { f: 440, b: 0.5 }, { f: 392, b: 0.5 }, { f: 330, b: 1 },
      { f: 294, b: 1 }, { f: 330, b: 1 }, { f: 294, b: 0.5 }, { f: 247, b: 0.5 }, { f: 294, b: 1 },
      { f: 330, b: 1 }, { f: 262, b: 1 }, { f: 220, b: 0.5 }, { f: 262, b: 0.5 }, { f: 330, b: 1 },
      { f: 349, b: 1 }, { f: 330, b: 1 }, { f: 262, b: 2 },
      { f: 392, b: 1 }, { f: 523, b: 1 }, { f: 494, b: 1 }, { f: 523, b: 1 },
      { f: 494, b: 1 }, { f: 440, b: 1 }, { f: 392, b: 1 }, { f: 330, b: 1 },
      { f: 349, b: 1 }, { f: 392, b: 1 }, { f: 440, b: 1 }, { f: 392, b: 1 },
      { f: 330, b: 1.5 }, { f: 294, b: 0.5 }, { f: 262, b: 2 },
      // B 段（8 小节，上扬变化）
      { f: 440, b: 1 }, { f: 523, b: 1 }, { f: 494, b: 1 }, { f: 440, b: 1 },
      { f: 523, b: 1 }, { f: 494, b: 1 }, { f: 440, b: 1 }, { f: 392, b: 1 },
      { f: 523, b: 1 }, { f: 587, b: 1 }, { f: 523, b: 1 }, { f: 494, b: 1 },
      { f: 494, b: 1 }, { f: 440, b: 1 }, { f: 392, b: 1 }, { f: 494, b: 1 },
      { f: 440, b: 0.5 }, { f: 494, b: 0.5 }, { f: 523, b: 1 }, { f: 494, b: 1 }, { f: 392, b: 1 },
      { f: 523, b: 1 }, { f: 494, b: 1 }, { f: 440, b: 1 }, { f: 392, b: 1 },
      { f: 392, b: 1 }, { f: 440, b: 1 }, { f: 494, b: 1 }, { f: 587, b: 1 },
      { f: 523, b: 1.5 }, { f: 392, b: 0.5 }, { f: 330, b: 2 },
    ],
    bass: [
      { f: 131, b: 2 }, { f: 98, b: 2 },
      { f: 98, b: 2 }, { f: 147, b: 2 },
      { f: 110, b: 2 }, { f: 165, b: 2 },
      { f: 87, b: 2 }, { f: 131, b: 2 },
      { f: 131, b: 2 }, { f: 196, b: 2 },
      { f: 98, b: 2 }, { f: 147, b: 2 },
      { f: 87, b: 2 }, { f: 131, b: 2 },
      { f: 131, b: 2 }, { f: 131, b: 2 },
      { f: 110, b: 2 }, { f: 165, b: 2 },
      { f: 87, b: 2 }, { f: 131, b: 2 },
      { f: 131, b: 2 }, { f: 196, b: 2 },
      { f: 98, b: 2 }, { f: 147, b: 2 },
      { f: 110, b: 2 }, { f: 165, b: 2 },
      { f: 87, b: 2 }, { f: 131, b: 2 },
      { f: 98, b: 2 }, { f: 147, b: 2 },
      { f: 131, b: 2 }, { f: 98, b: 2 },
    ],
    // 16 小节和声：C G Am F C G F C | Am F C G Am F G C
    chords: [
      { notes: [262, 330, 392], beats: 4 },
      { notes: [196, 247, 294], beats: 4 },
      { notes: [220, 262, 330], beats: 4 },
      { notes: [175, 220, 262], beats: 4 },
      { notes: [262, 330, 392], beats: 4 },
      { notes: [196, 247, 294], beats: 4 },
      { notes: [175, 220, 262], beats: 4 },
      { notes: [262, 330, 392], beats: 4 },
      { notes: [220, 262, 330], beats: 4 },
      { notes: [175, 220, 262], beats: 4 },
      { notes: [262, 330, 392], beats: 4 },
      { notes: [196, 247, 294], beats: 4 },
      { notes: [220, 262, 330], beats: 4 },
      { notes: [175, 220, 262], beats: 4 },
      { notes: [196, 247, 294], beats: 4 },
      { notes: [262, 330, 392], beats: 4 },
    ],
    drums: {
      kick: [0, 2],
      snare: [1, 3],
      hat: [0.5, 1, 1.5, 2, 2.5, 3],
      vol: { kick: 0.5, snare: 0.25, hat: 0.13 },
      fill: { beats: [3.5, 3.75], every: 4 }, // 每 4 小节末鼓花
    },
  },
  // 舒缓：原创氛围旋律，级进长句，无鼓仅镲
  calm: {
    bpm: 72,
    volume: 0.11,
    filterFreq: 1600,
    melodyWave: "sine",
    attack: 0.08, // 慢起音更柔和
    melodyOctave: false,
    accent: false,
    padVolume: 0.3,
    padWave: "sine",
    arpEvery: 2, // 琶音每 2 拍一音，轻柔铺垫
    arpVolume: 0.24,
    arpWave: "sine",
    reverb: { delay: 0.34, feedback: 0.38, wet: 0.2 },
    melody: [
      // A 段
      { f: 330, b: 2 }, { f: 392, b: 2 },
      { f: 440, b: 2 }, { f: 392, b: 2 },
      { f: 349, b: 2 }, { f: 330, b: 2 },
      { f: 262, b: 2 }, { f: 294, b: 2 },
      { f: 330, b: 2 }, { f: 392, b: 2 },
      { f: 494, b: 2 }, { f: 440, b: 2 },
      { f: 392, b: 2 }, { f: 349, b: 2 },
      { f: 330, b: 4 },
      // B 段（音区抬高）
      { f: 523, b: 2 }, { f: 494, b: 2 },
      { f: 392, b: 2 }, { f: 330, b: 2 },
      { f: 440, b: 2 }, { f: 392, b: 2 },
      { f: 330, b: 2 }, { f: 294, b: 2 },
      { f: 349, b: 2 }, { f: 392, b: 2 },
      { f: 523, b: 2 }, { f: 587, b: 2 },
      { f: 494, b: 2 }, { f: 440, b: 2 },
      { f: 330, b: 4 },
    ],
    bass: [
      { f: 131, b: 4 }, { f: 110, b: 4 }, { f: 87, b: 4 }, { f: 131, b: 4 },
      { f: 110, b: 4 }, { f: 98, b: 4 }, { f: 87, b: 4 }, { f: 131, b: 4 },
      { f: 87, b: 4 }, { f: 131, b: 4 }, { f: 98, b: 4 }, { f: 110, b: 4 },
      { f: 87, b: 4 }, { f: 131, b: 4 }, { f: 98, b: 4 }, { f: 131, b: 4 },
    ],
    // 16 小节和声：C Am F C Am G F C | F C G Am F C G C
    chords: [
      { notes: [262, 330, 392], beats: 4 },
      { notes: [220, 262, 330], beats: 4 },
      { notes: [175, 220, 262], beats: 4 },
      { notes: [262, 330, 392], beats: 4 },
      { notes: [220, 262, 330], beats: 4 },
      { notes: [196, 247, 294], beats: 4 },
      { notes: [175, 220, 262], beats: 4 },
      { notes: [262, 330, 392], beats: 4 },
      { notes: [175, 220, 262], beats: 4 },
      { notes: [262, 330, 392], beats: 4 },
      { notes: [196, 247, 294], beats: 4 },
      { notes: [220, 262, 330], beats: 4 },
      { notes: [175, 220, 262], beats: 4 },
      { notes: [262, 330, 392], beats: 4 },
      { notes: [196, 247, 294], beats: 4 },
      { notes: [262, 330, 392], beats: 4 },
    ],
    drums: {
      hat: [0, 2], // 仅极轻踩镲标点，无鼓
      vol: { hat: 0.06 },
    },
  },
  // 律动：原创切分动机，Em-G-D-C 推进，节奏感强
  march: {
    bpm: 112,
    volume: 0.1,
    filterFreq: 1500,
    melodyWave: "square", // 经低通滤波后呈复古合成器感
    melodyOctave: true,
    accent: true,
    padVolume: 0.2,
    padWave: "triangle",
    arpEvery: 1,
    arpVolume: 0.32,
    arpWave: "triangle",
    reverb: { delay: 0.2, feedback: 0.25, wet: 0.1 },
    melody: [
      // A 段
      { f: 330, b: 0.5 }, { f: 330, b: 0.5 }, { f: 294, b: 1 }, { f: 247, b: 1 }, { f: 262, b: 1 },
      { f: 247, b: 0.5 }, { f: 294, b: 0.5 }, { f: 330, b: 1 }, { f: 294, b: 1 }, { f: 330, b: 1 },
      { f: 294, b: 0.5 }, { f: 294, b: 0.5 }, { f: 247, b: 1 }, { f: 220, b: 1 }, { f: 262, b: 1 },
      { f: 262, b: 1 }, { f: 294, b: 1 }, { f: 330, b: 2 },
      { f: 392, b: 0.5 }, { f: 392, b: 0.5 }, { f: 330, b: 1 }, { f: 294, b: 1 }, { f: 330, b: 1 },
      { f: 330, b: 0.5 }, { f: 392, b: 0.5 }, { f: 330, b: 1 }, { f: 247, b: 1 }, { f: 294, b: 1 },
      { f: 294, b: 1 }, { f: 220, b: 1 }, { f: 247, b: 2 },
      { f: 262, b: 2 }, { f: 247, b: 2 },
      // B 段（高音区再现）
      { f: 494, b: 0.5 }, { f: 494, b: 0.5 }, { f: 440, b: 1 }, { f: 392, b: 1 }, { f: 440, b: 1 },
      { f: 392, b: 0.5 }, { f: 440, b: 0.5 }, { f: 494, b: 1 }, { f: 440, b: 1 }, { f: 392, b: 1 },
      { f: 440, b: 0.5 }, { f: 440, b: 0.5 }, { f: 392, b: 1 }, { f: 330, b: 1 }, { f: 294, b: 1 },
      { f: 392, b: 1 }, { f: 440, b: 1 }, { f: 494, b: 2 },
      { f: 523, b: 0.5 }, { f: 494, b: 0.5 }, { f: 440, b: 1 }, { f: 392, b: 1 }, { f: 440, b: 1 },
      { f: 440, b: 0.5 }, { f: 392, b: 0.5 }, { f: 330, b: 1 }, { f: 294, b: 1 }, { f: 330, b: 1 },
      { f: 330, b: 1 }, { f: 294, b: 1 }, { f: 247, b: 2 },
      { f: 330, b: 1 }, { f: 294, b: 1 }, { f: 262, b: 2 },
    ],
    bass: [
      { f: 165, b: 2 }, { f: 123, b: 2 },
      { f: 98, b: 2 }, { f: 147, b: 2 },
      { f: 147, b: 2 }, { f: 110, b: 2 },
      { f: 131, b: 2 }, { f: 98, b: 2 },
      { f: 165, b: 2 }, { f: 123, b: 2 },
      { f: 98, b: 2 }, { f: 147, b: 2 },
      { f: 147, b: 2 }, { f: 110, b: 2 },
      { f: 131, b: 2 }, { f: 98, b: 2 },
      { f: 165, b: 2 }, { f: 123, b: 2 },
      { f: 98, b: 2 }, { f: 147, b: 2 },
      { f: 147, b: 2 }, { f: 110, b: 2 },
      { f: 131, b: 2 }, { f: 98, b: 2 },
      { f: 165, b: 2 }, { f: 123, b: 2 },
      { f: 98, b: 2 }, { f: 147, b: 2 },
      { f: 147, b: 2 }, { f: 110, b: 2 },
      { f: 131, b: 2 }, { f: 98, b: 2 },
    ],
    // Em-G-D-C ×4
    chords: [
      { notes: [247, 330, 392], beats: 4 },
      { notes: [196, 247, 294], beats: 4 },
      { notes: [147, 185, 220], beats: 4 },
      { notes: [262, 330, 392], beats: 4 },
      { notes: [247, 330, 392], beats: 4 },
      { notes: [196, 247, 294], beats: 4 },
      { notes: [147, 185, 220], beats: 4 },
      { notes: [262, 330, 392], beats: 4 },
      { notes: [247, 330, 392], beats: 4 },
      { notes: [196, 247, 294], beats: 4 },
      { notes: [147, 185, 220], beats: 4 },
      { notes: [262, 330, 392], beats: 4 },
      { notes: [247, 330, 392], beats: 4 },
      { notes: [196, 247, 294], beats: 4 },
      { notes: [147, 185, 220], beats: 4 },
      { notes: [262, 330, 392], beats: 4 },
    ],
    drums: {
      kick: [0, 1.5, 2.5], // 切分底鼓
      snare: [1, 3],
      hat: [0.5, 1, 1.5, 2, 2.5, 3], // 密集踩镲
      vol: { kick: 0.55, snare: 0.28, hat: 0.13 },
      fill: { beats: [3.25, 3.5, 3.75], every: 4 },
    },
  },
};

class GameAudioManager {
  constructor() {
    this.enabled = true;
    this.unlocked = false;
    this._zhVoice = null;
    this._unlockHandler = null;

    this._speechQueue = [];
    this._speechBusy = false;

    this._audioContext = null;
    this._bgmSource = null;
    this._bgmGain = null;
    this._bufferCache = {};
    this._currentLoopScene = null;
    this._playingBgmTimer = null;

    this._oneShotAudio = null;
    this._bgmSynthTimer = null;
    this._bgmSynthStyle = null;
    this._bgmToken = 0;
    this._bgmChainNodes = [];
    this._noiseBuffer = null;

    // 试听独立通道（不占用游戏 BGM 状态，避免竞态叠加）
    this._previewAudio = null;
    this._previewGain = null;
    this._previewNodes = [];
    this._previewTimer = null;
    this._resumeBgmAfterPreview = false;

    /** 用户音效偏好：bgm=classic|sunny|calm|march|off，correct/wrong=classic|合成风格 */
    this.soundConfig = {
      bgm: "classic",
      correct: "classic",
      wrong: "classic",
    };
  }

  /** 设置音效风格；BGM 风格变化且正在播放时自动重启应用新速率 */
  setSoundConfig(config) {
    if (!config) return;
    const prevBgm = this.soundConfig.bgm;
    this.soundConfig = { ...this.soundConfig, ...config };

    if (
      config.bgm !== undefined &&
      config.bgm !== prevBgm &&
      this._currentLoopScene === "playing"
    ) {
      this._stopBgm();
      this._playLoopBgm("playing");
    }
  }

  init() {
    this._initVoices();
  }

  _initVoices() {
    if (!window.speechSynthesis) return;
    const pickVoice = () => {
      const voices = window.speechSynthesis.getVoices();
      if (!voices.length) return;
      const zhVoices = voices.filter((v) => v.lang && v.lang.startsWith("zh"));
      this._zhVoice =
        zhVoices.find((v) => VOICE_PREFER.some((name) => v.name.includes(name))) ||
        zhVoices.find((v) => v.localService) ||
        zhVoices[0] ||
        null;
      console.info("[GameAudio] 中文语音:", this._zhVoice?.name || "系统默认");
    };
    pickVoice();
    window.speechSynthesis.onvoiceschanged = pickVoice;
  }

  _ensureContext() {
    if (!this._audioContext) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      this._audioContext = new Ctx();
    }
    if (this._audioContext.state === "suspended") {
      this._audioContext.resume().catch((err) => {
        console.error("[GameAudio] AudioContext 恢复失败", err);
      });
    }
    return this._audioContext;
  }

  async _loadBuffer(scene) {
    if (this._bufferCache[scene]) return this._bufferCache[scene];
    const ctx = this._ensureContext();
    if (!ctx) return null;
    const config = SCENE_CONFIG[scene];
    if (!config || !config.loop) return null;
    try {
      const res = await fetch(config.src);
      const arrayBuffer = await res.arrayBuffer();
      const buffer = await ctx.decodeAudioData(arrayBuffer);
      this._bufferCache[scene] = buffer;
      return buffer;
    } catch (err) {
      console.error("[GameAudio] 加载音频缓冲失败:", scene, err);
      return null;
    }
  }

  setEnabled(enabled) {
    this.enabled = enabled;
    if (!enabled) this.stopAll();
  }

  unlock() {
    if (this.unlocked) return;
    this.unlocked = true;
    this._ensureContext();
    console.info("[GameAudio] 音频已解锁");
    if (this._unlockHandler) {
      document.removeEventListener("touchstart", this._unlockHandler);
      document.removeEventListener("click", this._unlockHandler);
      this._unlockHandler = null;
    }
  }

  bindUnlockOnInteraction() {
    if (this.unlocked) return;
    this._unlockHandler = () => this.unlock();
    document.addEventListener("touchstart", this._unlockHandler, { once: true, passive: true });
    document.addEventListener("click", this._unlockHandler, { once: true, passive: true });
  }

  /**
   * 人声播报（队列顺序播放，避免互相打断显得生硬）
   * @param {string} text
   * @param {'result'|'default'} style
   */
  speak(text, style = "default") {
    if (!this.enabled || !this.unlocked || !text) return Promise.resolve();
    if (!window.speechSynthesis) {
      console.error("[GameAudio] 当前环境不支持语音合成");
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      this._speechQueue.push({ text, style, resolve });
      this._processSpeechQueue();
    });
  }

  _applySpeechStyle(utterance, style) {
    if (this._zhVoice) utterance.voice = this._zhVoice;

    if (style === "result") {
      utterance.rate = 0.78;
      utterance.pitch = 0.9;
      utterance.volume = 0.82;
    } else {
      utterance.rate = 0.92;
      utterance.pitch = 1.0;
      utterance.volume = 0.95;
    }
  }

  _processSpeechQueue() {
    if (this._speechBusy || !this._speechQueue.length) return;
    if (!window.speechSynthesis) return;

    const { text, style, resolve } = this._speechQueue.shift();
    this._speechBusy = true;

    try {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = "zh-CN";
      this._applySpeechStyle(utterance, style);

      utterance.onend = () => {
        this._speechBusy = false;
        resolve();
        this._processSpeechQueue();
      };
      utterance.onerror = (err) => {
        console.error("[GameAudio] 人声播报失败", err);
        this._speechBusy = false;
        resolve();
        this._processSpeechQueue();
      };

      window.speechSynthesis.speak(utterance);
      console.info("[GameAudio] 人声播报:", text);
    } catch (err) {
      console.error("[GameAudio] 人声播报异常", err);
      this._speechBusy = false;
      resolve();
      this._processSpeechQueue();
    }
  }

  _clearSpeechQueue() {
    this._speechQueue = [];
    this._speechBusy = false;
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  }

  playCountdownTick(number) {
    if (!this.enabled || !this.unlocked) return;
    const config = COUNTDOWN_AUDIO[number];
    if (!config) return;

    this._stopOneShot();
    try {
      const audio = new Audio(config.src);
      audio.volume = config.volume;
      this._oneShotAudio = audio;
      audio.play().catch((err) => {
        console.error("[GameAudio] 倒计时音频播放失败:", number, err);
      });
      console.info("[GameAudio] 播放倒计时音频:", number);
    } catch (err) {
      console.error("[GameAudio] 倒计时音频异常:", number, err);
    }
  }

  /** 限时赛进入最后 10 秒时播放提示音 */
  playTimeLimitWarning() {
    if (!this.enabled || !this.unlocked) return;
    console.info("[GameAudio] 播放限时赛提示：还剩十秒");
    this._playAudio(TIME_LIMIT_WARNING);
  }

  _duckPlayingBgm(durationSec = 0.75) {
    if (this._currentLoopScene !== "playing" || !this._bgmGain || !this._audioContext) return;
    const gain = this._bgmGain.gain;
    const now = this._audioContext.currentTime;
    const normal = SCENE_CONFIG.playing.volume;
    const ducked = normal * 0.25;
    gain.cancelScheduledValues(now);
    gain.setValueAtTime(ducked, now);
    gain.linearRampToValueAtTime(normal, now + durationSec);
  }

  _playFeedbackSound(config) {
    if (!this.enabled || !this.unlocked) return;
    if (!config?.src) return;
    this._duckPlayingBgm();
    try {
      const audio = new Audio(config.src);
      audio.volume = config.volume ?? 0.85;
      audio.play().catch((err) => {
        console.error("[GameAudio] 答题提示音播放失败", err);
      });
    } catch (err) {
      console.error("[GameAudio] 答题提示音异常", err);
    }
  }

  playAnswerCorrect() {
    console.info("[GameAudio] 播放答对提示音:", this.soundConfig.correct);
    this._playAnswerSound(this.soundConfig.correct, ANSWER_FEEDBACK.correct);
  }

  playAnswerWrong() {
    console.info("[GameAudio] 播放答错提示音:", this.soundConfig.wrong);
    this._playAnswerSound(this.soundConfig.wrong, ANSWER_FEEDBACK.wrong);
  }

  /** 单词水果忍者：切中（明亮三和弦快速琶音） */
  playNinjaSlice() {
    if (!this.enabled || !this.unlocked) return;
    const ctx = this._ensureContext();
    if (!ctx) return;
    try {
      [523.25, 659.25, 783.99].forEach((freq, i) => {
        this._playTone(ctx, {
          type: "triangle",
          freq,
          start: i * 0.035,
          duration: 0.2,
          volume: 0.32,
        });
      });
    } catch (err) {
      console.error("[GameAudio] 忍者切中音效异常", err);
    }
  }

  /** 单词水果忍者：切错（低频锯齿短鸣） */
  playNinjaMiss() {
    if (!this.enabled || !this.unlocked) return;
    const ctx = this._ensureContext();
    if (!ctx) return;
    try {
      this._playTone(ctx, {
        type: "sawtooth",
        freq: 220,
        endFreq: 130,
        duration: 0.28,
        volume: 0.3,
      });
    } catch (err) {
      console.error("[GameAudio] 忍者切错音效异常", err);
    }
  }

  /** 单词水果忍者：连击提示（随连击数上升的双音） */
  playNinjaCombo(combo) {
    if (!this.enabled || !this.unlocked) return;
    const ctx = this._ensureContext();
    if (!ctx) return;
    try {
      const base = 440 + Math.min(combo, 8) * 60;
      this._playTone(ctx, { type: "sine", freq: base, duration: 0.14, volume: 0.28 });
      this._playTone(ctx, {
        type: "sine",
        freq: base * 1.25,
        start: 0.09,
        duration: 0.18,
        volume: 0.28,
      });
    } catch (err) {
      console.error("[GameAudio] 忍者连击音效异常", err);
    }
  }

  /** 按风格播放答题音效：classic 用预录 wav，其余用 Web Audio 合成 */
  _playAnswerSound(style, fallbackConfig) {
    if (!this.enabled || !this.unlocked) return;
    if (!style || style === "classic") {
      this._playFeedbackSound(fallbackConfig);
      return;
    }
    this._duckPlayingBgm();
    const ctx = this._ensureContext();
    if (!ctx) {
      this._playFeedbackSound(fallbackConfig);
      return;
    }
    try {
      if (fallbackConfig === ANSWER_FEEDBACK.correct) {
        this._playCorrectSynth(style, ctx);
      } else {
        this._playWrongSynth(style, ctx);
      }
    } catch (err) {
      console.error("[GameAudio] 合成音效异常，回退预录音", err);
      this._playFeedbackSound(fallbackConfig);
    }
  }

  /** 合成单个音符：支持起止频率（滑音）与延时 */
  _playTone(ctx, { type = "sine", freq, endFreq, start = 0, duration = 0.2, volume = 0.5 }) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const t0 = ctx.currentTime + start;

    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    if (endFreq && endFreq !== freq) {
      osc.frequency.linearRampToValueAtTime(endFreq, t0 + duration);
    }

    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.linearRampToValueAtTime(volume, t0 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + duration + 0.05);
  }

  /** 答对合成风格 */
  _playCorrectSynth(style, ctx) {
    switch (style) {
      case "chime": // 风铃：三角波快速上行琶音
        [
          [1047, 0],
          [1319, 0.08],
          [1568, 0.16],
        ].forEach(([f, s]) =>
          this._playTone(ctx, { type: "triangle", freq: f, start: s, duration: 0.35, volume: 0.5 })
        );
        break;
      case "fanfare": // 号角：锯齿波有力上行三连音
        [
          [523, 0],
          [659, 0.12],
          [784, 0.24],
        ].forEach(([f, s]) =>
          this._playTone(ctx, { type: "sawtooth", freq: f, start: s, duration: 0.28, volume: 0.4 })
        );
        break;
      case "sparkle": // 星光：高频正弦琶音
        [
          [1568, 0],
          [1976, 0.06],
          [2637, 0.12],
        ].forEach(([f, s]) =>
          this._playTone(ctx, { type: "sine", freq: f, start: s, duration: 0.3, volume: 0.45 })
        );
        break;
      default:
        break;
    }
  }

  /** 答错合成风格 */
  _playWrongSynth(style, ctx) {
    switch (style) {
      case "buzz": // 蜂鸣：低频方波两短音
        this._playTone(ctx, { type: "square", freq: 220, start: 0, duration: 0.14, volume: 0.3 });
        this._playTone(ctx, { type: "square", freq: 220, start: 0.19, duration: 0.14, volume: 0.3 });
        break;
      case "descend": // 下滑：正弦滑音
        this._playTone(ctx, { type: "sine", freq: 350, endFreq: 120, start: 0, duration: 0.4, volume: 0.5 });
        break;
      case "thud": // 低音双响：低沉但清晰可辨（三角波带泛音，小扬声器也能出声）
        this._playTone(ctx, { type: "triangle", freq: 262, endFreq: 240, start: 0, duration: 0.22, volume: 0.7 });
        this._playTone(ctx, { type: "triangle", freq: 196, endFreq: 175, start: 0.24, duration: 0.3, volume: 0.7 });
        break;
      default:
        break;
    }
  }

  /** 设置页试听：kind = bgm | correct | wrong */
  previewSound(kind, style) {
    if (!this.enabled) return;
    this.unlock();
    if (kind === "correct") {
      this._playAnswerSound(style, ANSWER_FEEDBACK.correct);
    } else if (kind === "wrong") {
      this._playAnswerSound(style, ANSWER_FEEDBACK.wrong);
    } else if (kind === "bgm") {
      this._previewBgm(style);
    }
  }

  /**
   * 试听背景音乐（独立通道）
   * 不占用 _bgmSource/_bgmGain/_currentLoopScene 等游戏 BGM 状态，
   * 连续点击只会替换试听内容，不会产生叠加或影响游戏中 BGM。
   */
  _previewBgm(style) {
    if (style === "off") return; // 关闭无可试听
    this.unlock();

    // 先清理上一次试听（若上次为游戏中试听，会先恢复游戏 BGM）
    this._stopPreview();

    // 游戏中试听：暂停游戏 BGM，试听结束后按当前配置恢复
    const gameBgmActive = this._currentLoopScene === "playing";
    if (gameBgmActive) this._stopBgm();
    this._resumeBgmAfterPreview = gameBgmActive;

    if (style === "classic") {
      // 经典：用 Audio 元素播放片段
      try {
        const audio = new Audio(SCENE_CONFIG.playing.src);
        audio.volume = 0.3;
        audio.play().catch((err) => console.error("[GameAudio] 经典 BGM 试听失败", err));
        this._previewAudio = audio;
        this._previewTimer = setTimeout(() => this._stopPreview(), 3000);
      } catch (err) {
        console.error("[GameAudio] 经典 BGM 试听失败", err);
      }
      return;
    }

    const config = SYNTH_BGM[style];
    const ctx = this._ensureContext();
    if (!config || !ctx) return;

    try {
      this._previewChainNodes = [];
      const gain = this._createSynthChain(ctx, config, Math.max(config.volume, 0.22), this._previewChainNodes);
      this._previewGain = gain;

      const beat = 60 / config.bpm;
      const attack = config.attack || 0.02;
      const start = ctx.currentTime + 0.08;
      let end = start;

      // 试听片段：旋律取前 16 个音符（约 4 小节完整乐句，听得出编曲效果）
      const melodyCount = Math.min(config.melody.length, 16);
      let t = start;
      let elapsed = 0;
      for (let i = 0; i < melodyCount; i++) {
        const note = config.melody[i];
        if (note.f > 0) {
          const dur = note.b * beat * 0.92;
          const velocity = config.accent && elapsed % 4 === 0 ? 1 : 0.78;
          this._spawnSynthNote(ctx, gain, note.f, t, dur, {
            wave: config.melodyWave,
            gain: 0.9,
            attack: Math.min(attack, note.b * beat * 0.3),
            detune: !!config.detune,
            octave: !!config.melodyOctave,
            velocity,
          });
        }
        t += note.b * beat;
        elapsed += note.b;
      }
      end = t;

      // 低音/琶音声部按时间轴截取
      const fillVoice = (notes, opts) => {
        if (!notes || !notes.length) return;
        let vt = start;
        let vi = 0;
        while (vt < end) {
          const note = notes[vi % notes.length];
          if (note.f > 0) {
            this._spawnSynthNote(ctx, gain, note.f, vt, note.b * beat * 0.92, {
              ...opts,
              attack: Math.min(opts.attack, note.b * beat * 0.3),
            });
          }
          vt += note.b * beat;
          vi++;
        }
      };
      fillVoice(config.bass, { wave: "sine", gain: 0.55, attack });
      fillVoice(this._expandArp(config), {
        wave: config.arpWave || "sine",
        gain: config.arpVolume || 0.3,
        attack,
      });

      // 和弦铺底按时间轴截取（延音包络）
      if (config.padVolume > 0 && config.chords) {
        let pt = start;
        let pi = 0;
        while (pt < end) {
          const chord = config.chords[pi % config.chords.length];
          const dur = Math.min(chord.beats * beat * 0.98, end - pt);
          if (dur > 0.15) {
            chord.notes.forEach((f) => {
              this._spawnSynthNote(ctx, gain, f, pt, dur, {
                wave: config.padWave || "sine",
                gain: config.padVolume,
                attack: Math.min(0.35, dur * 0.35),
                sustain: true,
              });
            });
          }
          pt += chord.beats * beat;
          pi++;
        }
      }

      // 打击乐按小节填充
      if (config.drums) {
        const evts = this._expandDrumEvents(config.drums);
        const vol = config.drums.vol || {};
        let barStart = start;
        while (barStart < end) {
          evts.forEach((e) => {
            const t0 = barStart + e.at * beat;
            if (t0 < end) this._spawnDrum(ctx, gain, e.type, t0, vol[e.type] || 0.2);
          });
          barStart += 4 * beat;
        }
      }

      // 片段播放完自动清理并恢复游戏 BGM
      const totalMs = (end - ctx.currentTime + 0.3) * 1000;
      this._previewTimer = setTimeout(() => this._stopPreview(), totalMs);
    } catch (err) {
      console.error("[GameAudio] 合成 BGM 试听失败", err);
    }
  }

  /** 停止试听并清理资源；若试听前游戏 BGM 在播放则恢复 */
  _stopPreview() {
    if (this._previewTimer) {
      clearTimeout(this._previewTimer);
      this._previewTimer = null;
    }
    if (this._previewAudio) {
      try {
        this._previewAudio.pause();
      } catch (err) {
        /* ignore */
      }
      this._previewAudio = null;
    }
    if (this._previewGain) {
      try {
        this._previewGain.disconnect();
      } catch (err) {
        /* ignore */
      }
      this._previewGain = null;
    }
    // 断开试听链路中间节点
    (this._previewChainNodes || []).forEach((node) => {
      try {
        node.disconnect();
      } catch (err) {
        /* ignore */
      }
    });
    this._previewChainNodes = [];
    this._previewNodes = [];

    if (this._resumeBgmAfterPreview) {
      this._resumeBgmAfterPreview = false;
      this._playLoopBgm("playing");
    }
  }

  _clearPlayingBgmTimer() {
    if (this._playingBgmTimer) {
      clearTimeout(this._playingBgmTimer);
      this._playingBgmTimer = null;
    }
  }

  /** 等「开始」音效播完后再无缝循环 BGM */
  playPlayingBgmAfterStart() {
    if (!this.enabled || !this.unlocked) return;
    this._clearPlayingBgmTimer();
    this._playingBgmTimer = setTimeout(() => {
      this._playingBgmTimer = null;
      this.playPlayingBgm();
    }, 680);
  }

  /** 进行中：柔和欢快无缝循环 BGM */
  async playPlayingBgm() {
    await this._playLoopBgm("playing");
  }

  /** 一方完成时播放紧迫危机循环提醒 */
  async playCrisisAlert() {
    await this._playLoopBgm("crisis");
  }

  async _playLoopBgm(scene) {
    if (!this.enabled || !this.unlocked) return;
    // 进行中 BGM 支持用户风格：off 则不播放
    if (scene === "playing") {
      const style = this.soundConfig.bgm;
      if (style === "off") return;

      // 合成旋律风格（sunny/calm/march）
      if (SYNTH_BGM[style]) {
        if (this._currentLoopScene === scene && this._bgmSynthStyle === style) return;
        this._stopBgm();
        if (this._startSynthBgm(style)) {
          console.info("[GameAudio] 合成循环 BGM:", style);
          return;
        }
        // 合成失败回退经典预录 BGM
      } else {
        if (this._currentLoopScene === scene && !this._bgmSynthStyle) return;
        this._stopBgm();
      }
    } else {
      if (this._currentLoopScene === scene) return;
      this._stopBgm();
    }

    // token 防竞态：await 期间若有新的播放/停止调用，本次作废，避免并发叠加
    const token = ++this._bgmToken;
    const ctx = this._ensureContext();
    const buffer = await this._loadBuffer(scene);
    if (token !== this._bgmToken) return;
    if (!ctx || !buffer) {
      console.error("[GameAudio] 无法播放循环 BGM:", scene);
      return;
    }

    try {
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;

      const gain = ctx.createGain();
      gain.gain.value = SCENE_CONFIG[scene].volume;

      source.connect(gain);
      gain.connect(ctx.destination);
      source.start(0);

      this._bgmSource = source;
      this._bgmGain = gain;
      this._currentLoopScene = scene;
      this._bgmSynthStyle = null;
      console.info("[GameAudio] 无缝循环播放:", scene);
    } catch (err) {
      console.error("[GameAudio] 循环 BGM 播放失败:", scene, err);
    }
  }

  /**
   * 合成单音，返回振荡器数组
   * opts: wave 波形 / gain 音量 / attack 起音 / detune 失谐加倍（加厚）
   *       velocity 力度（重音） / octave 高八度加倍（旋律更亮）/ sustain 延音包络（铺底）
   */
  _spawnSynthNote(ctx, dest, freq, t0, dur, opts) {
    const {
      wave = "triangle",
      gain: vol = 0.9,
      attack = 0.02,
      detune = false,
      velocity = 1,
      octave = false,
      sustain = false,
    } = opts || {};
    const oscs = [];
    const v = vol * velocity;
    const noteGain = ctx.createGain();
    noteGain.gain.setValueAtTime(0.0001, t0);
    noteGain.gain.linearRampToValueAtTime(v, t0 + attack);
    if (sustain) {
      // 延音包络：保持后释放，用于和弦铺底
      const rel = Math.min(0.3, dur * 0.4);
      noteGain.gain.setValueAtTime(v, Math.max(t0 + attack, t0 + dur - rel));
      noteGain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    } else {
      noteGain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    }
    noteGain.connect(dest);

    const spawn = (f, cents, oscGain) => {
      const osc = ctx.createOscillator();
      osc.type = wave;
      osc.frequency.value = f;
      if (cents) osc.detune.value = cents;
      if (oscGain !== undefined) {
        const g = ctx.createGain();
        g.gain.value = oscGain;
        osc.connect(g);
        g.connect(noteGain);
      } else {
        osc.connect(noteGain);
      }
      osc.start(t0);
      osc.stop(t0 + dur + 0.05);
      oscs.push(osc);
    };
    spawn(freq);
    if (detune) spawn(freq, 7, 0.7); // 失谐副振荡器
    if (octave) spawn(freq * 2, 0, 0.22); // 高八度加倍
    return oscs;
  }

  /** 建立合成 BGM 主链（增益 + 低通滤波 + 反馈延迟混响），返回 gain 节点，中间节点收集到 chainNodes */
  _createSynthChain(ctx, config, volume, chainNodes) {
    const gain = ctx.createGain();
    gain.gain.value = volume;
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = config.filterFreq || 2000;
    filter.Q.value = 0.5;
    gain.connect(filter);
    filter.connect(ctx.destination);
    chainNodes.push(filter);

    // 反馈延迟混响：营造空间感，避免干瘪
    if (config.reverb) {
      const delay = ctx.createDelay(1);
      delay.delayTime.value = config.reverb.delay;
      const fb = ctx.createGain();
      fb.gain.value = config.reverb.feedback;
      const wet = ctx.createGain();
      wet.gain.value = config.reverb.wet;
      filter.connect(delay);
      delay.connect(fb);
      fb.connect(delay);
      delay.connect(wet);
      wet.connect(ctx.destination);
      chainNodes.push(delay, fb, wet);
    }
    return gain;
  }

  /** 白噪声缓冲（打击乐音源，缓存复用） */
  _getNoiseBuffer(ctx) {
    if (this._noiseBuffer) return this._noiseBuffer;
    const len = Math.floor(ctx.sampleRate * 0.3);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    this._noiseBuffer = buf;
    return buf;
  }

  /** 打击乐单次发声：kick=底鼓（低频滑落），snare=军鼓（中频噪声），hat=踩镲（高频短噪声） */
  _spawnDrum(ctx, dest, type, t0, vol) {
    if (type === "kick") {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(150, t0);
      osc.frequency.exponentialRampToValueAtTime(45, t0 + 0.12);
      g.gain.setValueAtTime(vol, t0);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.16);
      osc.connect(g);
      g.connect(dest);
      osc.start(t0);
      osc.stop(t0 + 0.2);
      return;
    }
    const src = ctx.createBufferSource();
    src.buffer = this._getNoiseBuffer(ctx);
    const g = ctx.createGain();
    const f = ctx.createBiquadFilter();
    if (type === "snare") {
      f.type = "bandpass";
      f.frequency.value = 1800;
      f.Q.value = 0.8;
      g.gain.setValueAtTime(vol, t0);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.12);
    } else {
      f.type = "highpass";
      f.frequency.value = 6000;
      g.gain.setValueAtTime(vol, t0);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.05);
    }
    src.connect(f);
    f.connect(g);
    g.connect(dest);
    src.start(t0);
    src.stop(t0 + 0.15);
  }

  /** 展开和弦琶音序列：每 arpEvery 拍弹出和弦下一音（上行循环） */
  _expandArp(config) {
    const notes = [];
    const per = config.arpEvery || 1;
    (config.chords || []).forEach((chord) => {
      let i = 0;
      for (let b = 0; b < chord.beats; b += per) {
        notes.push({ f: chord.notes[i % chord.notes.length], b: per });
        i++;
      }
    });
    return notes;
  }

  /** 展开 4 拍小节的打击乐事件表 */
  _expandDrumEvents(drums) {
    const evts = [];
    (drums.kick || []).forEach((p) => evts.push({ type: "kick", at: p }));
    (drums.snare || []).forEach((p) => evts.push({ type: "snare", at: p }));
    (drums.hat || []).forEach((p) => evts.push({ type: "hat", at: p }));
    evts.sort((a, b) => a.at - b.at);
    return evts;
  }

  /** 启动合成旋律 BGM（旋律 + 低音 + 琶音 + 打击乐多声部循环调度），返回是否成功 */
  _startSynthBgm(style) {
    const config = SYNTH_BGM[style];
    if (!config) return false;
    const ctx = this._ensureContext();
    if (!ctx) return false;

    try {
      // 主增益挂到 _bgmGain，答题时 duck 逻辑同样生效
      this._bgmChainNodes = [];
      const gain = this._createSynthChain(ctx, config, config.volume, this._bgmChainNodes);
      this._bgmGain = gain;

      const beat = 60 / config.bpm;
      const attack = config.attack || 0.02;
      const isAlive = () => this._currentLoopScene === "playing" && this.soundConfig.bgm === style;

      // 音高声部（旋律/低音/琶音）各自独立循环，支持重音力度
      const makeVoice = (notes, opts) => {
        if (!notes || !notes.length) return null;
        let idx = 0;
        let elapsed = 0; // 累计拍数，用于小节头重音
        let nextTime = ctx.currentTime + 0.15;
        const tick = () => {
          if (!isAlive()) return;
          const note = notes[idx % notes.length];
          if (note.f > 0) {
            const velocity = opts.accent && elapsed % 4 === 0 ? 1 : 0.78;
            this._spawnSynthNote(ctx, gain, note.f, nextTime, note.b * beat * 0.92, {
              ...opts,
              velocity,
              attack: Math.min(opts.attack, note.b * beat * 0.3),
            });
          }
          nextTime += note.b * beat;
          elapsed += note.b;
          idx++;
          // 提前约 0.3 秒调度下一个音符
          this._bgmSynthTimer = setTimeout(
            tick,
            Math.max(0, (nextTime - ctx.currentTime - 0.3) * 1000)
          );
        };
        return tick;
      };

      const melodyTick = makeVoice(config.melody, {
        wave: config.melodyWave,
        gain: 0.9,
        attack,
        detune: !!config.detune,
        octave: !!config.melodyOctave,
        accent: !!config.accent,
      });
      const bassTick = makeVoice(config.bass, {
        wave: "sine",
        gain: 0.55,
        attack,
        accent: !!config.accent,
      });
      const arpTick = makeVoice(this._expandArp(config), {
        wave: config.arpWave || "sine",
        gain: config.arpVolume || 0.3,
        attack,
      });

      // 和弦铺底声部：每个和弦持续发声（慢起慢收的柱式长音，撑起和声厚度）
      const makePad = () => {
        if (!config.chords || !(config.padVolume > 0)) return null;
        let idx = 0;
        let nextTime = ctx.currentTime + 0.15;
        const tick = () => {
          if (!isAlive()) return;
          const chord = config.chords[idx % config.chords.length];
          const dur = chord.beats * beat * 0.98;
          chord.notes.forEach((f) => {
            this._spawnSynthNote(ctx, gain, f, nextTime, dur, {
              wave: config.padWave || "sine",
              gain: config.padVolume,
              attack: Math.min(0.35, dur * 0.35),
              sustain: true,
            });
          });
          nextTime += chord.beats * beat;
          idx++;
          this._bgmSynthTimer = setTimeout(
            tick,
            Math.max(0, (nextTime - ctx.currentTime - 0.3) * 1000)
          );
        };
        return tick;
      };
      const padTick = makePad();

      // 打击乐声部：按 4 拍小节整小节调度，每 every 小节末加鼓花
      const makeDrums = () => {
        if (!config.drums) return null;
        const evts = this._expandDrumEvents(config.drums);
        if (!evts.length) return null;
        const vol = config.drums.vol || {};
        const fill = config.drums.fill;
        const fillEvery = (fill && fill.every) || 4;
        let barIdx = 0;
        let barStart = ctx.currentTime + 0.15;
        const tick = () => {
          if (!isAlive()) return;
          evts.forEach((e) => {
            this._spawnDrum(ctx, gain, e.type, barStart + e.at * beat, vol[e.type] || 0.2);
          });
          // 小节末鼓花（军鼓连击），推进乐句感
          if (fill && barIdx % fillEvery === fillEvery - 1) {
            fill.beats.forEach((p) => {
              this._spawnDrum(ctx, gain, "snare", barStart + p * beat, (vol.snare || 0.2) * 0.85);
            });
          }
          barStart += 4 * beat;
          barIdx++;
          // 下一小节提前 0.3 秒调度
          this._bgmSynthTimer = setTimeout(
            tick,
            Math.max(0, (barStart - 4 * beat - ctx.currentTime - 0.3) * 1000)
          );
        };
        return tick;
      };
      const drumsTick = makeDrums();

      if (melodyTick) melodyTick();
      if (bassTick) bassTick();
      if (arpTick) arpTick();
      if (padTick) padTick();
      if (drumsTick) drumsTick();

      this._currentLoopScene = "playing";
      this._bgmSynthStyle = style;
      return true;
    } catch (err) {
      console.error("[GameAudio] 合成 BGM 启动失败", err);
      return false;
    }
  }

  _playOneShot(scene) {
    if (!this.enabled || !this.unlocked) return;
    const config = SCENE_CONFIG[scene];
    if (!config) return;
    this._playAudio(config);
  }

  _playAudio(config, onEnd) {
    if (!this.enabled || !this.unlocked) return;
    if (!config?.src) return;

    this._stopOneShot();
    try {
      const audio = new Audio(config.src);
      audio.volume = config.volume ?? 0.8;
      if (config.playbackRate) {
        audio.playbackRate = config.playbackRate;
        audio.preservesPitch = true;
      }
      this._oneShotAudio = audio;
      if (typeof onEnd === "function") {
        audio.onended = onEnd;
      }
      audio.play().catch((err) => {
        console.error("[GameAudio] 音频播放失败:", err);
        if (typeof onEnd === "function") onEnd();
      });
    } catch (err) {
      console.error("[GameAudio] 音频播放异常", err);
      if (typeof onEnd === "function") onEnd();
    }
  }

  /**
   * 结算：有胜负时柔和人声 + 庆祝音；平局仅庆祝音
   * @param {'player1'|'player2'|'tie'|null} winner
   */
  playResults(winner) {
    if (!this.enabled || !this.unlocked) return;

    this._stopBgm();

    const playSting = () => {
      console.info("[GameAudio] 播放胜利庆祝音");
      this._playOneShot("results");
    };

    if (winner === "player1") {
      console.info("[GameAudio] 播放玩家一胜利播报");
      this._playAudio(RESULTS_VOICE.player1, playSting);
    } else if (winner === "player2") {
      console.info("[GameAudio] 播放玩家二胜利播报");
      this._playAudio(RESULTS_VOICE.player2, playSting);
    } else {
      playSting();
    }
  }

  _stopBgm() {
    // 使进行中的 classic 加载/播放请求作废
    this._bgmToken++;
    if (this._bgmSource) {
      try {
        this._bgmSource.stop();
        this._bgmSource.disconnect();
      } catch (err) {
        console.error("[GameAudio] 停止循环提醒失败", err);
      }
      this._bgmSource = null;
    }
    if (this._bgmSynthTimer) {
      clearTimeout(this._bgmSynthTimer);
      this._bgmSynthTimer = null;
    }
    this._bgmSynthStyle = null;
    if (this._bgmGain) {
      try {
        this._bgmGain.disconnect();
      } catch (err) {
        /* ignore */
      }
      this._bgmGain = null;
    }
    // 断开滤波/混响等中间节点，释放资源
    this._bgmChainNodes.forEach((node) => {
      try {
        node.disconnect();
      } catch (err) {
        /* ignore */
      }
    });
    this._bgmChainNodes = [];
    this._currentLoopScene = null;
  }

  _stopOneShot() {
    if (this._oneShotAudio) {
      try {
        this._oneShotAudio.pause();
        this._oneShotAudio.currentTime = 0;
      } catch (err) {
        console.error("[GameAudio] 停止单次音效失败", err);
      }
      this._oneShotAudio = null;
    }
  }

  stopAll() {
    this._clearSpeechQueue();
    this._clearPlayingBgmTimer();
    this._resumeBgmAfterPreview = false; // 全部停止，无需恢复游戏 BGM
    this._stopPreview();
    this._stopBgm();
    this._stopOneShot();
  }

  destroy() {
    this.stopAll();
    this._bufferCache = {};
    if (this._audioContext) {
      this._audioContext.close().catch(() => {});
      this._audioContext = null;
    }
    if (this._unlockHandler) {
      document.removeEventListener("touchstart", this._unlockHandler);
      document.removeEventListener("click", this._unlockHandler);
      this._unlockHandler = null;
    }
  }
}

const gameAudioManager = new GameAudioManager();
export default gameAudioManager;
